import { useState } from "react";
import { Button, Checkbox, Input, Modal, Select } from "@legal-500/legal500-design-system";
import type {
  AwardBulkChanges,
  AwardShortlistTag,
  AwardState,
  AwardStatus,
} from "../../../api/awards.types.js";
import { useAwardDictionaries } from "../../../hooks/useAwards.js";
import {
  AWARD_SHORTLIST_TAG_OPTIONS,
  AWARD_STATUS_OPTIONS,
  YEAR_MAX,
  YEAR_MIN,
} from "../awardForm.helpers.js";
import styles from "./BulkEditModal.module.css";

type BulkEditModalProps = {
  selectedCount: number;
  submitting: boolean;
  onApply: (changes: AwardBulkChanges) => void;
  onClose: () => void;
};

type FieldKey = keyof AwardBulkChanges;

const FIELD_KEYS: FieldKey[] = ["productId", "seriesId", "year", "status", "shortlistTag", "state"];

/** The shortlist tag select's third real choice: "clear" goes out as `null`,
 * the wire's spelling for "remove the tag", which no HTML option can carry. */
const CLEAR_SHORTLIST_TAG = "clear";

/** What each control holds before it is ticked. The selects start on their
 * placeholder so a ticked-but-untouched Product cannot silently send the first
 * dictionary entry; the state select has no placeholder because both of its
 * values are real choices. */
type FieldValues = {
  productId: string;
  seriesId: string;
  year: number;
  status: AwardStatus | "";
  shortlistTag: AwardShortlistTag | typeof CLEAR_SHORTLIST_TAG | "";
  state: AwardState;
};

const STATUS_SELECT_OPTIONS = [{ value: "", label: "Select status..." }, ...AWARD_STATUS_OPTIONS];

const SHORTLIST_TAG_SELECT_OPTIONS = [
  { value: "", label: "Select shortlist tag..." },
  ...AWARD_SHORTLIST_TAG_OPTIONS,
  { value: CLEAR_SHORTLIST_TAG, label: "Clear" },
];

const STATE_SELECT_OPTIONS: Array<{ value: AwardState; label: string }> = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

/**
 * One partial change for every selected award.
 *
 * Each row pairs a "change this field" checkbox with its control, and the
 * control stays disabled until the box is ticked: the body sent is exactly the
 * ticked keys, so the tick is the user's statement that the column should move
 * at all, separate from what it should move to. The request itself belongs to
 * the page, which owns the selection, the refetch and the error banner.
 */
export function BulkEditModal({ selectedCount, submitting, onApply, onClose }: BulkEditModalProps) {
  const dictionaries = useAwardDictionaries();

  const [enabled, setEnabled] = useState<Record<FieldKey, boolean>>({
    productId: false,
    seriesId: false,
    year: false,
    status: false,
    shortlistTag: false,
    state: false,
  });
  const [values, setValues] = useState<FieldValues>({
    productId: "",
    seriesId: "",
    year: new Date().getFullYear(),
    status: "",
    shortlistTag: "",
    state: "draft",
  });

  const toggle = (key: FieldKey) => setEnabled(current => ({ ...current, [key]: !current[key] }));

  const setValue = <K extends keyof FieldValues>(key: K, value: FieldValues[K]) =>
    setValues(current => ({ ...current, [key]: value }));

  const isValid: Record<FieldKey, boolean> = {
    productId: values.productId !== "",
    seriesId: values.seriesId !== "",
    year: Number.isInteger(values.year) && values.year >= YEAR_MIN && values.year <= YEAR_MAX,
    status: values.status !== "",
    shortlistTag: values.shortlistTag !== "",
    state: true,
  };

  const tickedKeys = FIELD_KEYS.filter(key => enabled[key]);
  const canApply = tickedKeys.length > 0 && tickedKeys.every(key => isValid[key]) && !submitting;

  const buildChanges = (): AwardBulkChanges => {
    const changes: AwardBulkChanges = {};
    if (enabled.productId) changes.productId = values.productId;
    if (enabled.seriesId) changes.seriesId = values.seriesId;
    if (enabled.year) changes.year = values.year;
    if (enabled.status && values.status !== "") changes.status = values.status;
    if (enabled.shortlistTag && values.shortlistTag !== "") {
      changes.shortlistTag =
        values.shortlistTag === CLEAR_SHORTLIST_TAG ? null : values.shortlistTag;
    }
    if (enabled.state) changes.state = values.state;
    return changes;
  };

  // Closing is refused while the request runs, for the reason ConfirmationModal
  // gives: dismissing would not stop the write, only hide where its outcome lands.
  const handleClose = () => {
    if (!submitting) onClose();
  };

  // The two dictionary rows wait for the dictionaries; the other four do not
  // need them and stay usable while the request is out or has failed.
  const dictionariesReady = dictionaries.data !== null;
  const productOptions = [
    { value: "", label: "Select product..." },
    ...(dictionaries.data?.products ?? []).map(entry => ({ value: entry.id, label: entry.name })),
  ];
  const seriesOptions = [
    { value: "", label: "Select series..." },
    ...(dictionaries.data?.series ?? []).map(entry => ({ value: entry.id, label: entry.name })),
  ];

  return (
    <Modal isOpen={true} onClose={handleClose} title="Bulk Edit" size="medium" blurBackground>
      <div className={styles.body}>
        {dictionaries.loading && <p className={styles.hint}>Loading dictionaries...</p>}
        {dictionaries.error && (
          <p className={styles.error}>
            Couldn't load the product and series lists. The other fields can still be changed.
          </p>
        )}

        <div className={styles.fieldRow}>
          <Checkbox
            aria-label="Change Product"
            checked={enabled.productId}
            onChange={() => toggle("productId")}
            disabled={!dictionariesReady || submitting}
          />
          <Select
            id="bulk-product"
            label="Product"
            options={productOptions}
            value={values.productId}
            onChange={event => setValue("productId", event.target.value)}
            disabled={!enabled.productId || submitting}
            fullWidth
          />
        </div>

        <div className={styles.fieldRow}>
          <Checkbox
            aria-label="Change Award Series"
            checked={enabled.seriesId}
            onChange={() => toggle("seriesId")}
            disabled={!dictionariesReady || submitting}
          />
          <Select
            id="bulk-series"
            label="Award Series"
            options={seriesOptions}
            value={values.seriesId}
            onChange={event => setValue("seriesId", event.target.value)}
            disabled={!enabled.seriesId || submitting}
            fullWidth
          />
        </div>

        <div className={styles.fieldRow}>
          <Checkbox
            aria-label="Change Year"
            checked={enabled.year}
            onChange={() => toggle("year")}
            disabled={submitting}
          />
          <Input
            id="bulk-year"
            label="Year"
            type="number"
            inputMode="numeric"
            min={YEAR_MIN}
            max={YEAR_MAX}
            step={1}
            value={Number.isNaN(values.year) ? "" : values.year}
            // An emptied box becomes NaN rather than 0, so the range check above
            // rejects it instead of accepting the year 0.
            onChange={event =>
              setValue("year", event.target.value === "" ? Number.NaN : Number(event.target.value))
            }
            error={
              enabled.year && !isValid.year
                ? `Year must be a whole number between ${YEAR_MIN} and ${YEAR_MAX}`
                : undefined
            }
            disabled={!enabled.year || submitting}
            fullWidth
          />
        </div>

        <div className={styles.fieldRow}>
          <Checkbox
            aria-label="Change Status"
            checked={enabled.status}
            onChange={() => toggle("status")}
            disabled={submitting}
          />
          <Select
            id="bulk-status"
            label="Status"
            options={STATUS_SELECT_OPTIONS}
            value={values.status}
            onChange={event => setValue("status", event.target.value as AwardStatus | "")}
            disabled={!enabled.status || submitting}
            fullWidth
          />
        </div>

        {/* Whether the selected rows may carry a tag is the server's call — it
            answers with the count that may not — so nothing here pre-checks
            statuses the modal cannot see. */}
        <div className={styles.fieldRow}>
          <Checkbox
            aria-label="Change Shortlist tag"
            checked={enabled.shortlistTag}
            onChange={() => toggle("shortlistTag")}
            disabled={submitting}
          />
          <Select
            id="bulk-shortlist-tag"
            label="Shortlist tag"
            options={SHORTLIST_TAG_SELECT_OPTIONS}
            value={values.shortlistTag}
            onChange={event =>
              setValue("shortlistTag", event.target.value as FieldValues["shortlistTag"])
            }
            disabled={!enabled.shortlistTag || submitting}
            fullWidth
          />
        </div>

        <div className={styles.fieldRow}>
          <Checkbox
            aria-label="Change Publish Status"
            checked={enabled.state}
            onChange={() => toggle("state")}
            disabled={submitting}
          />
          <Select
            id="bulk-state"
            label="Publish Status"
            options={STATE_SELECT_OPTIONS}
            value={values.state}
            onChange={event => setValue("state", event.target.value as AwardState)}
            disabled={!enabled.state || submitting}
            fullWidth
          />
        </div>

        <p className={styles.footerNote}>
          Only ticked fields are changed on {selectedCount} awards. Untouched fields keep their
          values.
        </p>

        <div className={styles.footer}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onApply(buildChanges())}
            disabled={!canApply}
          >
            {submitting ? "Applying..." : "Apply"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
